// PDF Report Generator using jsPDF
import jsPDF from 'jspdf';

/**
 * Generate a PDF progress report
 * @param {Object} progressData - User progress data
 */
export const generateProgressPDF = (progressData) => {
  const { userProgress, earnedBadges, totalStats, date } = progressData;
  
  // Create new PDF document
  const doc = new jsPDF();
  
  // Set up colors
  const primaryColor = [52, 152, 219]; // Blue
  const accentColor = [46, 204, 113]; // Green
  const textColor = [44, 62, 80]; // Dark gray
  
  // Title
  doc.setFontSize(24);
  doc.setTextColor(...primaryColor);
  doc.text('Kids Learning Progress Report', 105, 20, { align: 'center' });
  
  // Date
  doc.setFontSize(12);
  doc.setTextColor(...textColor);
  doc.text(`Generated on: ${date}`, 105, 30, { align: 'center' });
  
  // Divider line
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(20, 35, 190, 35);
  
  // Overall Statistics
  doc.setFontSize(16);
  doc.setTextColor(...accentColor);
  doc.text('Overall Statistics', 20, 45);
  
  doc.setFontSize(11);
  doc.setTextColor(...textColor);
  let yPos = 55;
  
  doc.text(`Total Quizzes Completed: ${totalStats.totalQuizzes}`, 25, yPos);
  yPos += 8;
  doc.text(`Average Score: ${totalStats.averageScore}%`, 25, yPos);
  yPos += 8;
  doc.text(`Badges Earned: ${earnedBadges.length}`, 25, yPos);
  yPos += 8;
  doc.text(`Strongest Category: ${totalStats.strongestCategory || 'N/A'}`, 25, yPos);
  yPos += 8;
  doc.text(`Area for Improvement: ${totalStats.weakestCategory || 'N/A'}`, 25, yPos);
  
  // Category Performance
  yPos += 15;
  doc.setFontSize(16);
  doc.setTextColor(...accentColor);
  doc.text('Category Performance', 20, yPos);
  
  yPos += 10;
  doc.setFontSize(11);
  doc.setTextColor(...textColor);
  
  // Get all categories that have been played
  const categories = Object.keys(userProgress).filter(cat => userProgress[cat] && userProgress[cat].totalQuizzes > 0);
  
  categories.forEach(category => {
    const data = userProgress[category];
    if (data) {
      // Check if we need a new page
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      const categoryName = category.charAt(0).toUpperCase() + category.slice(1);
      // Calculate average score correctly using totalPossibleScore
      const avgScore = data.totalPossibleScore > 0 
        ? Math.round((data.totalScore / data.totalPossibleScore) * 100)
        : 0;
      
      doc.setFontSize(13);
      doc.setTextColor(...primaryColor);
      doc.text(`${categoryName}:`, 25, yPos);
      
      doc.setFontSize(10);
      doc.setTextColor(...textColor);
      yPos += 6;
      doc.text(`  Quizzes Completed: ${data.totalQuizzes}`, 30, yPos);
      yPos += 5;
      doc.text(`  Best Score: ${Math.round(data.bestScore || 0)}%`, 30, yPos);
      yPos += 5;
      doc.text(`  Average Score: ${avgScore}%`, 30, yPos);
      yPos += 5;
      doc.text(`  Last Played: ${data.lastPlayed ? new Date(data.lastPlayed).toLocaleDateString() : 'Never'}`, 30, yPos);
      yPos += 10;
    }
  });
  
  // Check if we need a new page
  if (yPos > 240) {
    doc.addPage();
    yPos = 20;
  }
  
  // Earned Badges
  if (earnedBadges.length > 0) {
    doc.setFontSize(16);
    doc.setTextColor(...accentColor);
    doc.text('Earned Badges', 20, yPos);
    
    yPos += 10;
    doc.setFontSize(11);
    doc.setTextColor(...textColor);
    
    earnedBadges.forEach((badge, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(`${index + 1}. ${badge.name} - ${badge.description}`, 25, yPos);
      yPos += 7;
    });
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      105,
      290,
      { align: 'center' }
    );
    doc.text(
      'Kids Learning Hub - Making Learning Fun!',
      105,
      285,
      { align: 'center' }
    );
  }
  
  // Save the PDF
  doc.save(`learning-progress-${date.replace(/\//g, '-')}.pdf`);
};

/**
 * Generate a certificate PDF
 * @param {Object} certificateData - Certificate data
 */
export const generateCertificate = (certificateData) => {
  const { childName, achievement, date } = certificateData;
  
  const doc = new jsPDF({
    orientation: 'landscape'
  });
  
  // Background
  doc.setFillColor(255, 248, 220);
  doc.rect(0, 0, 297, 210, 'F');
  
  // Border
  doc.setDrawColor(218, 165, 32);
  doc.setLineWidth(3);
  doc.rect(10, 10, 277, 190);
  
  doc.setLineWidth(1);
  doc.rect(15, 15, 267, 180);
  
  // Title
  doc.setFontSize(36);
  doc.setTextColor(218, 165, 32);
  doc.text('Certificate of Achievement', 148.5, 50, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(14);
  doc.setTextColor(100, 100, 100);
  doc.text('This is to certify that', 148.5, 70, { align: 'center' });
  
  // Name
  doc.setFontSize(28);
  doc.setTextColor(52, 152, 219);
  doc.text(childName, 148.5, 90, { align: 'center' });
  
  // Achievement
  doc.setFontSize(14);
  doc.setTextColor(100, 100, 100);
  doc.text('has successfully achieved', 148.5, 105, { align: 'center' });
  
  doc.setFontSize(20);
  doc.setTextColor(46, 204, 113);
  doc.text(achievement, 148.5, 125, { align: 'center' });
  
  // Date
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date: ${date}`, 148.5, 150, { align: 'center' });
  
  // Footer
  doc.setFontSize(10);
  doc.text('Kids Learning Hub', 148.5, 180, { align: 'center' });
  
  // Save
  doc.save(`certificate-${childName.replace(/\s/g, '-')}.pdf`);
};
